class SearchDrawer extends HTMLElement {
  constructor() {
    super();

    this.panel = this.querySelector('.search-drawer__panel');
    this.input = this.querySelector('.search-drawer__input');
    this.opener = null;

    this.products = this.querySelector('[data-search-drawer-products]');
    this.results = this.querySelector('[data-search-drawer-results]');
    this.heading = this.querySelector('[data-search-drawer-heading]');
    this.defaultMarkup = this.results ? this.results.innerHTML : '';
    this.abortController = null;
    this.debounce = null;

    this.onKeyUp = this.onKeyUp.bind(this);
    this.onOverlayClick = this.onOverlayClick.bind(this);
    this.onInput = this.onInput.bind(this);
  }

  connectedCallback() {
    const overlay = this.querySelector('[data-search-drawer-overlay]');
    if (overlay) overlay.addEventListener('click', this.onOverlayClick);

    this.querySelectorAll('[data-search-drawer-close]').forEach((button) => {
      button.addEventListener('click', () => this.close());
    });

    if (this.input) this.input.addEventListener('input', this.onInput);

    this.addEventListener('keyup', this.onKeyUp);
  }

  onInput() {
    clearTimeout(this.debounce);
    this.debounce = setTimeout(() => this.search(this.input.value.trim()), 300);
  }

  getExcludeTagsClause() {
    return (this.dataset.excludedTags || '')
      .split(',')
      .map((tag) => tag.trim())
      .filter(Boolean)
      .map((tag) => `-tag:"${tag.replace(/"/g, '')}"`)
      .join(' ');
  }

  trim(limit) {
    const items = this.results.querySelectorAll('.search-drawer__product');
    items.forEach((item, index) => {
      if (index >= limit) item.remove();
    });
  }

  restore() {
    if (!this.results) return;
    this.results.innerHTML = this.defaultMarkup;
    this.products.classList.remove('is-loading');
    if (this.heading) this.heading.textContent = this.products.dataset.defaultHeading;
  }

  async search(term) {
    if (!this.results) return;

    if (this.abortController) this.abortController.abort();

    if (term === '') {
      this.restore();
      return;
    }

    const base = (window.routes && window.routes.predictive_search_url) || '/search/suggest';
    const limit = parseInt(this.products.dataset.resultsLimit, 10) || 6;
    const query = [`*${term}*`, this.getExcludeTagsClause()].filter(Boolean).join(' ');
    const url = `${base}?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=10&section_id=search-drawer-results`;

    this.abortController = new AbortController();
    this.products.classList.add('is-loading');

    try {
      const response = await fetch(url, { signal: this.abortController.signal });
      if (!response.ok) throw new Error(response.status);

      const text = await response.text();
      const doc = new DOMParser().parseFromString(text, 'text/html');
      const section = doc.querySelector('#shopify-section-search-drawer-results');

      this.results.innerHTML = section ? section.innerHTML : '';
      this.trim(limit);
      if (this.heading) this.heading.textContent = this.products.dataset.resultsHeading;
    } catch (error) {
      if (error.name === 'AbortError') return;
      this.restore();
    } finally {
      this.products.classList.remove('is-loading');
    }
  }

  disconnectedCallback() {
    document.body.classList.remove('overflow-hidden');
  }

  isOpen() {
    return this.classList.contains('is-open');
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;
    this.close();
  }

  onOverlayClick() {
    this.close();
  }

  open(opener) {
    if (this.isOpen()) return;

    this.opener = opener || null;
    this.classList.add('is-open');
    document.body.classList.add('overflow-hidden');

    if (typeof trapFocus === 'function') {
      trapFocus(this.panel, this.input || this.panel);
    } else if (this.input) {
      this.input.focus();
    }
  }

  close() {
    if (!this.isOpen()) return;

    this.classList.remove('is-open');
    document.body.classList.remove('overflow-hidden');

    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(this.opener);
    } else if (this.opener) {
      this.opener.focus();
    }

    this.opener = null;
  }
}

if (!customElements.get('search-drawer')) {
  customElements.define('search-drawer', SearchDrawer);
}

(() => {
  const TRIGGER = '[data-search-drawer-open]';

  function drawer() {
    return document.querySelector('[data-search-drawer]');
  }

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest(TRIGGER);
    if (!trigger) return;

    const target = drawer();
    if (!target) return;

    event.preventDefault();
    target.open(trigger);
  });
})();
